import { CircleHelpIcon } from 'lucide-react'
import { type PropsWithChildren } from 'react'

import { type MetalTier } from 'components/prospects/types'
import { Select, SelectContent, SelectItem, SelectTrigger } from 'components/ui/select'
import { Slider } from 'components/ui/slider'
import { Tooltip } from 'slab'
import { getFormattedDate, numberToOrdinal } from 'utils'
import { type QuotingQuoteQuoteType } from 'utils/web_api.schemas'

import { QuoteExplorerMode, type WorkflowType } from './QuoteExplorer'
import UpdateBenchmarks from './UpdateBenchmarks'

type ConfigurationSectionProps = {
  title: string
  titleTooltip?: string
  includeDivider?: boolean
  trailingComponent?: React.ReactNode
}

const ConfigurationSection = ({
  children,
  title,
  titleTooltip,
  includeDivider = true,
  trailingComponent,
}: PropsWithChildren<ConfigurationSectionProps>) => {
  return (
    <div className='mt-4'>
      <h4 className='text-gray-700 font-medium text-sm my-2 flex items-center'>
        {title}
        {titleTooltip && (
          <div className='flex-grow'>
            <Tooltip content={titleTooltip} side='right'>
              <CircleHelpIcon className='w-4 h-4 ml-2' />
            </Tooltip>
          </div>
        )}
        {trailingComponent && (
          trailingComponent
        )}
      </h4>
      {children}
      {includeDivider && <hr className='my-4 border-gray-200' />}
    </div>
  )
}

export const QuoteConfigurationSliders = (
  {
    mode,
    availableCoveragePercentiles,
    selectedCoveragePercentile,
    availableMetalTiers,
    selectedMetalTier,
    setSelectedMetalTier,
    employerEmployeeContribution,
    employerSpouseContribution,
    employerChildContribution,
    setEmployerEmployeeContribution,
    setEmployerSpouseContribution,
    setEmployerChildContribution,
    setSelectedCoveragePercentile,
    coverageStartDate,
    prospectId,
    quoteId,
    workflowType,
    quoteType,
  } : {
    mode: QuoteExplorerMode,
    employerEmployeeContribution: number,
    employerSpouseContribution: number,
    employerChildContribution: number,
    setEmployerEmployeeContribution: (value: number) => void,
    setEmployerSpouseContribution: (value: number) => void,
    setEmployerChildContribution: (value: number) => void,
    selectedCoveragePercentile: number,
    setSelectedCoveragePercentile: (value: number) => void,
    selectedMetalTier: string,
    setSelectedMetalTier: (value: string) => void,
    availableCoveragePercentiles: string[],
    availableMetalTiers: string[],
    coverageStartDate: string | null,
    prospectId: string,
    quoteId: string,
    workflowType: WorkflowType,
    quoteType?: QuotingQuoteQuoteType,
  }
) => {
  const sortedAvailableCoveragePercentiles = availableCoveragePercentiles.map(cp => parseInt(cp)).sort((a, b) => a - b)
  const isExploreMode = mode === QuoteExplorerMode.explore
  const canUpdateBenchmarks = mode === QuoteExplorerMode.edit && !!coverageStartDate && workflowType === 'sales'

  return (
    <>
      <ConfigurationSection
        title='Benchmark'
        titleTooltip='The benchmark defines the level of coverage you want to base your allowance amounts on. For example, "Gold 100" means you want to calculate allowance against the very best Gold plans in their area, while "Gold 25" means you want to calculate your allowances against the best 25% of Gold plans in their area.'
        trailingComponent={
          canUpdateBenchmarks && (
            <UpdateBenchmarks
              prospectId={prospectId}
              quoteId={quoteId}
              preserveInitialValues={quoteType === 'renewal'}
              quoteGenerationOptions={{
                metal_tiers: availableMetalTiers as MetalTier[],
                coverage_percentiles: availableCoveragePercentiles,
                coverage_start_date: coverageStartDate,
              }}
            />
          )
        }
      >
        {isExploreMode
          ? (
            <p className='text-gray-700 text-sm'>
              Covering up to the {numberToOrdinal(selectedCoveragePercentile)} percentile of {selectedMetalTier} plans
            </p>
            )
          : (
            <>
              <p className='text-gray-700 text-xs'>I want to cover up to the:</p>
              <div className='flex items-center gap-2 flex-wrap'>
                <Select
                  value={selectedCoveragePercentile?.toString()}
                  onValueChange={(value) => setSelectedCoveragePercentile(parseInt(value))}
                  data-testid='coverage-percentile-select'
                >
                  <SelectTrigger
                    className='group w-fit rounded-none border-none outline-none p-0 focus:ring-0 focus:ring-offset-0 bg-transparent cursor-pointer transition-colors duration-300'
                    data-testid='coverage-percentile-select-trigger'
                  >
                    <span className='border-b border-gray-700 border-dotted group-hover:border-b-gray-800 italic text-xs text-gray-700 group-hover:text-gray-900 whitespace-nowrap'>
                      {numberToOrdinal(selectedCoveragePercentile)}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {sortedAvailableCoveragePercentiles.map((coveragePercentile) => (
                      <SelectItem
                        key={`${coveragePercentile}`}
                        value={`${coveragePercentile}`}
                      >
                        {numberToOrdinal(coveragePercentile)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className='text-xs text-gray-700 whitespace-nowrap'>percentile of</span>
                <Select
                  value={selectedMetalTier}
                  onValueChange={(value) => setSelectedMetalTier(value)}
                  data-testid='metal-tier-select'
                >
                  <SelectTrigger className='group w-fit rounded-none border-none outline-none p-0 focus:ring-0 focus:ring-offset-0 bg-transparent cursor-pointer transition-colors duration-300'>
                    <span className='border-b border-gray-700 border-dotted group-hover:border-b-gray-800 italic text-xs text-gray-700 group-hover:text-gray-900 whitespace-nowrap'>{selectedMetalTier}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {availableMetalTiers.map((metalTier) => (
                      <SelectItem key={metalTier} value={metalTier}>
                        {metalTier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className='text-xs text-gray-700 whitespace-nowrap'>plans</span>
              </div>
            </>
            )}
      </ConfigurationSection>
      <ConfigurationSection title='Cost sharing' titleTooltip='Cost sharing defines how the cost of insurance is split between the employer and employee.'>
        {isExploreMode
          ? (
            <>
              <p className='text-sm text-gray-700'>Employer pays <span className='tabular-nums'>{Math.round(employerEmployeeContribution * 100)}%</span></p>
              <p className='text-sm text-gray-700'>Employee pays <span className='tabular-nums'>{100 - Math.round(employerEmployeeContribution * 100)}%</span></p>
            </>
            )
          : (
            <div className='mt-4'>
              <>
                <Slider
                  size='small'
                  trackColor='bg-[#CFCFCF]'
                  min={0}
                  max={100}
                  value={[Math.round(employerEmployeeContribution * 100)]}
                  onValueChange={(value) => setEmployerEmployeeContribution(value[0] / 100)}
                  data-testid='employer-employee-contribution-slider'
                />
                <div className='flex items-center mt-2'>
                  <div className='bg-black rounded-full w-2 h-2' />
                  <p className='text-xs font-normal text-gray-500 ml-2'>
                    <span className='tabular-nums'>{Math.round(employerEmployeeContribution * 100)}%</span> employer contribution
                  </p>
                </div>
                <div className='flex items-center mt-2'>
                  <div className='bg-[#CFCFCF] rounded-full w-2 h-2' />
                  <p className='text-xs font-normal text-gray-500 ml-2'>
                    <span className='tabular-nums'>{100 - (Math.round(employerEmployeeContribution * 100))}%</span> employee contribution
                  </p>
                </div>
              </>
            </div>
            )}
      </ConfigurationSection>
      <ConfigurationSection title='Dependent coverage' titleTooltip="The percentage of health insurance cost you'll cover for your employees' dependents.">
        {isExploreMode
          ? (
            <>
              <p className='text-sm text-gray-700'>Covering <span className='tabular-nums'>{Math.round(employerSpouseContribution * 100)}%</span> for 1st dependent</p>
              <p className='text-sm text-gray-700'>Covering <span className='tabular-nums'>{Math.round(employerChildContribution * 100)}%</span> for additional dependents</p>
            </>
            )
          : (
            <>
              <div className='mt-4'>
                <Slider
                  size='small'
                  trackColor='bg-[#CFCFCF]'
                  min={0}
                  max={100}
                  value={[Math.round(employerSpouseContribution * 100)]}
                  onValueChange={(value) => setEmployerSpouseContribution(value[0] / 100)}
                  data-testid='employer-spouse-contribution-slider'
                />
                <p className='text-xs font-normal text-gray-500 mt-2'>
                  <span className='tabular-nums'>{Math.round(employerSpouseContribution * 100)}%</span> for 1st dependent
                </p>
              </div>
              <div className='mt-4'>
                <Slider
                  size='small'
                  trackColor='bg-[#CFCFCF]'
                  min={0}
                  max={100}
                  value={[Math.round(employerChildContribution * 100)]}
                  onValueChange={(value) => setEmployerChildContribution(value[0] / 100)}
                  data-testid='employer-child-contribution-slider'
                />
                <p className='text-xs font-normal text-gray-500 mt-2'>
                  <span className='tabular-nums'>{Math.round(employerChildContribution * 100)}%</span> for additional dependents
                </p>
              </div>
            </>
            )}
      </ConfigurationSection>
      <ConfigurationSection title='Coverage start date' includeDivider={false}>
        <p className='text-sm text-gray-700'>
          {coverageStartDate ? getFormattedDate(coverageStartDate, { timeZone: 'UTC' }) : '--'}
        </p>
      </ConfigurationSection>
    </>
  )
}

export default QuoteConfigurationSliders
